/**
 * Service Worker Registration Utility
 * 
 * Handles registration, updates, and lifecycle of the service worker
 * Disabled in private/incognito mode
 */

import { isPrivateModeSync } from '../utils/privateMode'

const isLocalhost = Boolean(
    window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
)

export function register(config) {
    // Don't register service worker in private mode
    if (isPrivateModeSync()) {
        return
    }

    if ('serviceWorker' in navigator) {
        // Wait for page load to prevent blocking initial render
        window.addEventListener('load', () => {
            const swUrl = `${import.meta.env.BASE_URL || '/'}service-worker.js`

            if (isLocalhost) {
                // This is running on localhost, check if a service worker still exists or not
                checkValidServiceWorker(swUrl, config)

                // Add some logging for localhost
                navigator.serviceWorker.ready.then(() => {
                })
            } else {
                // Is not localhost, just register service worker
                registerValidSW(swUrl, config)
            }
        })
    } else {
    }
}

function registerValidSW(swUrl, config) {
    navigator.serviceWorker
        .register(swUrl)
        .then((registration) => {

            // Check for updates
            registration.onupdatefound = () => {
                const installingWorker = registration.installing
                if (installingWorker == null) {
                    return
                }

                installingWorker.onstatechange = () => {
                    if (installingWorker.state === 'installed') {
                        if (navigator.serviceWorker.controller) {
                            // New content is available, notify user

                            // Execute callback if provided
                            if (config && config.onUpdate) {
                                config.onUpdate(registration)
                            }

                            // Optionally auto-update
                            if (config && config.autoUpdate) {
                                installingWorker.postMessage({ type: 'SKIP_WAITING' })
                                window.location.reload()
                            }
                        } else {
                            // Content is cached for offline use

                            if (config && config.onSuccess) {
                                config.onSuccess(registration)
                            }
                        }
                    }
                }
            }

            // Automatic update check every hour
            setInterval(() => {
                registration.update()
            }, 60 * 60 * 1000) // Check for updates every hour
        })
        .catch((error) => {
            console.error('[Service Worker] Registration failed:', error)
        })

    // Handle controller change (new service worker activated)
    let refreshing
    navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (refreshing) return
        refreshing = true
        window.location.reload()
    })
}

function checkValidServiceWorker(swUrl, config) {
    // Check if the service worker can be found
    fetch(swUrl, {
        headers: { 'Service-Worker': 'script' }
    })
        .then((response) => {
            // Ensure service worker exists, and that we really are getting a JS file
            const contentType = response.headers.get('content-type')
            if (
                response.status === 404 ||
                (contentType != null && contentType.indexOf('javascript') === -1)
            ) {
                // No service worker found, reload the page
                navigator.serviceWorker.ready.then((registration) => {
                    registration.unregister().then(() => {
                        window.location.reload()
                    })
                })
            } else {
                // Service worker found, proceed as normal
                registerValidSW(swUrl, config)
            }
        })
        .catch(() => {
        })
}

export function unregister() {
    if ('serviceWorker' in navigator) {
        navigator.serviceWorker.ready
            .then((registration) => {
                return registration.unregister()
            })
            .then(() => {
            })
            .catch((error) => {
                console.error('[Service Worker] Unregistration failed:', error.message)
            })
    }
}

export function clearCache() {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHE' })
    }
}

export async function getCacheSize() {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        return new Promise((resolve) => {
            const messageChannel = new MessageChannel()
            messageChannel.port1.onmessage = (event) => {
                resolve(event.data.size)
            }
            navigator.serviceWorker.controller.postMessage(
                { type: 'GET_CACHE_SIZE' },
                [messageChannel.port2]
            )
        })
    }
    return 0
}
